import { call, put, select } from 'redux-saga/effects'
import { createSagas } from 'src/helpers/redux'
import store from 'src/store'
import { realtimeService } from 'src/services/realtime'
import { presenceService } from 'src/services/presence'
import * as usersAuthService from 'src/services/auth/users'
import { IResponse } from 'src/services/auth/http'
import { setters } from './index'
import { actions, Actions } from './actions'

import * as Account from '../account'

const STATE_CHANNEL = 'auth:presence-state'
const LOCATION_CHANNEL = 'auth:presence-location'

const withoutMe = <T extends { id: number }>(users: T[] = [], myId?: number) =>
  users.filter((user) => user.id !== myId)

export default createSagas<Actions>(actions, {
  start: function* ({ payload }) {
    // The profile may come out of localStorage before the layout has opened the realtime
    // connection (child effects run first); connection() is idempotent
    realtimeService.connection()

    // idempotent as well: the profile is re-fetched on every account update
    presenceService.start(payload.channel)

    const myId: number | undefined = yield select(
      Account.selectors.selectors.userId
    )

    realtimeService.only<{ users: usersAuthService.ITeamMemberPresence[] }>(
      {
        key: 'presence:state',
        event: 'user_status',
        channel: STATE_CHANNEL,
      },
      (data) => {
        const users = data?.users ?? []

        store.dispatch(setters.applyStatusUpdates(withoutMe(users, myId)))

        // my status changed somewhere (another tab, the phone, the sweeper): the full own view,
        // manual flag included, only comes with the profile
        if (users.some((user) => user.id === myId)) {
          store.dispatch(Account.actions.getProfile())
        }
      }
    )

    yield put(actions.getTeam())
  },

  getTeam: function* () {
    const myId: number | undefined = yield select(
      Account.selectors.selectors.userId
    )
    const team: IResponse<usersAuthService.ITeamMemberPresence[]> = yield call(
      usersAuthService.getUsersPresence
    )

    yield put(setters.setTeam(withoutMe(team.data, myId)))
  },

  setStatus: function* ({ payload }) {
    const user: IResponse<usersAuthService.IGetAccountData> = yield call(
      usersAuthService.putUserStatus,
      payload.status
    )

    if (user.data?.presence) {
      yield put(Account.setters.setPresence(user.data.presence))
    }
  },

  watchLocations: {
    effect: 'latest',
    saga: function* ({ payload }) {
      const listener = {
        key: 'presence:location',
        event: 'user_location',
        channel: LOCATION_CHANNEL,
      }

      if (!payload.enabled) {
        realtimeService.offOnly(listener)

        return
      }

      const myId: number | undefined = yield select(
        Account.selectors.selectors.userId
      )

      realtimeService.only<{
        users: {
          id: number
          currentLocation: usersAuthService.IUserLocation | null
          rev: number
        }[]
      }>(listener, (data) => {
        store.dispatch(
          setters.applyLocationUpdates(withoutMe(data?.users, myId))
        )
      })

      // locations were not followed until now: the snapshot brings them up to date
      yield put(actions.getTeam())
    },
  },
})
